import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, formatDate } from "../lib/apiClient";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const TARGET_TYPES = ["all", "case", "user", "payment", "review", "diagnosis", "document", "feature_request", "normativa", "system_config", "commercial_contact_request", "secop_search"];

export default function AdminAuditPage() {
  const [filters, setFilters] = useState({ action: "", actor_user_id: "", target_type: "all", target_id: "", from_: "", to: "" });
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ total: 0, items: [], pages: 0 });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ page: String(page), limit: "100" });
      if (filters.action) p.set("action", filters.action);
      if (filters.actor_user_id) p.set("actor_user_id", filters.actor_user_id);
      if (filters.target_type !== "all") p.set("target_type", filters.target_type);
      if (filters.target_id) p.set("target_id", filters.target_id);
      if (filters.from_) p.set("from_", filters.from_);
      if (filters.to) p.set("to", filters.to);
      const { data: d } = await api.get(`/admin/audit?${p}`);
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error cargando audit");
    } finally { setLoading(false); }
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-audit-page">
      <p className="text-xs uppercase tracking-[0.22em] text-primary font-medium mb-1">Trazabilidad</p>
      <h1 className="font-serif text-3xl text-gray-900 mb-1">Auditoría</h1>
      <p className="text-xs text-gray-500 mb-5">{data.total} entradas · página {page} de {data.pages || 1}</p>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-4 items-end">
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Acción (regex)</label>
          <Input value={filters.action} onChange={(e) => setFilters((f) => ({ ...f, action: e.target.value }))} placeholder="case.transition…" className="rounded-sm h-9 text-xs" data-testid="audit-action" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Actor user_id</label>
          <Input value={filters.actor_user_id} onChange={(e) => setFilters((f) => ({ ...f, actor_user_id: e.target.value }))} placeholder="uuid" className="rounded-sm h-9 text-xs" data-testid="audit-actor" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Tipo target</label>
          <Select value={filters.target_type} onValueChange={(v) => setFilters((f) => ({ ...f, target_type: v }))}>
            <SelectTrigger className="rounded-sm h-9 text-xs" data-testid="audit-target-type"><SelectValue /></SelectTrigger>
            <SelectContent>{TARGET_TYPES.map((t) => <SelectItem key={t} value={t}>{t === "all" ? "Todos" : t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Target id</label>
          <Input value={filters.target_id} onChange={(e) => setFilters((f) => ({ ...f, target_id: e.target.value }))} placeholder="uuid" className="rounded-sm h-9 text-xs" data-testid="audit-target-id" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Desde</label>
          <Input type="date" value={filters.from_} onChange={(e) => setFilters((f) => ({ ...f, from_: e.target.value }))} className="rounded-sm h-9 text-xs" data-testid="audit-from" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Hasta</label>
          <Input type="date" value={filters.to} onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))} className="rounded-sm h-9 text-xs" data-testid="audit-to" />
        </div>
      </div>

      <div className="border border-gray-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-[11px] uppercase tracking-widest text-gray-500 bg-gray-50 border-b">
            <th className="px-3 py-3 text-left">Fecha</th>
            <th className="px-3 py-3 text-left">Acción</th>
            <th className="px-3 py-3 text-left">Actor</th>
            <th className="px-3 py-3 text-left">Target</th>
            <th className="px-3 py-3 text-right">Metadata</th>
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="px-4 py-10 text-center text-gray-500">Cargando…</td></tr>}
            {!loading && data.items.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-gray-500" data-testid="audit-empty">Sin entradas.</td></tr>
            )}
            {!loading && data.items.map((e) => {
              const isOpen = expanded === e.id;
              const caseLink = e.target_type === "case" && e.target_id ? `/admin/cases/${e.target_id}` : null;
              return (
                <React.Fragment key={e.id}>
                  <tr className="border-b border-gray-100 hover:bg-gray-50" data-testid={`audit-row-${e.id}`}>
                    <td className="px-3 py-2 text-[11px] text-gray-500 whitespace-nowrap">{formatDate(e.created_at)}</td>
                    <td className="px-3 py-2 font-mono text-[11px] text-gray-800">{e.action}</td>
                    <td className="px-3 py-2 text-xs text-gray-700 truncate max-w-[180px]">{e.actor_email || <span className="text-gray-400">{e.actor_user_id?.slice(0, 8) || "system"}</span>}</td>
                    <td className="px-3 py-2 text-xs">
                      <span className="font-mono text-[11px] text-gray-600">{e.target_type}</span>
                      {e.target_id && <span className="text-gray-400 font-mono text-[11px] ml-2">{e.target_id.slice(0, 8)}</span>}
                      {caseLink && <Link to={caseLink} className="ml-2 text-primary hover:underline">→</Link>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => setExpanded(isOpen ? null : e.id)} className="text-[11px] text-primary hover:underline" data-testid={`audit-toggle-${e.id}`}>
                        {isOpen ? "Ocultar" : "Ver"}
                      </button>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <td colSpan={5} className="px-4 py-3">
                        <pre className="text-[11px] font-mono text-gray-700 whitespace-pre-wrap" data-testid={`audit-meta-${e.id}`}>{JSON.stringify(e.metadata || {}, null, 2)}</pre>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {data.pages > 1 && (
        <div className="flex justify-between items-center mt-3 text-xs text-gray-600">
          <span>Página {page} de {data.pages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="rounded-sm h-8" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="audit-prev">Anterior</Button>
            <Button variant="outline" size="sm" className="rounded-sm h-8" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)} data-testid="audit-next">Siguiente</Button>
          </div>
        </div>
      )}
    </div>
  );
}
